import { lexer, type Token } from "marked";

/**
 * Incremental lexing for streaming answers.
 *
 * A fresh `text` arrives on every token, but earlier blocks are already complete
 * (terminated by a blank line), so we only `lexer()` the newly finished blocks
 * plus the unstable tail instead of the whole answer each time.
 *
 * A blank-line boundary is only committed when the chunk before it is a plain
 * block (not a list, blockquote, indented continuation, or open fence), since
 * those can be re-joined by later content.
 */
export interface LexCache {
  /** Text fully lexed into `tokens` (a prefix of the current `text`). */
  source: string;
  /** Block tokens for `source`; reused across streaming updates. */
  tokens: Token[];
  /** True when `tokens` came from a single whole-document lex. */
  final: boolean;
}

export function createLexCache(): LexCache {
  return { source: "", tokens: [], final: false };
}

/**
 * `final` re-lexes the whole document once (then caches it). Streaming lexes
 * fragments, which cannot resolve link reference definitions that span a
 * committed boundary; the final pass fixes that and any other cross-block
 * artifact.
 */
export function incrementalTokens(
  cache: LexCache,
  text: string,
  final = false,
): Token[] {
  if (final) {
    if (!cache.final || cache.source !== text) {
      cache.source = text;
      cache.tokens = [...lexer(text, { gfm: true })];
      cache.final = true;
    }
    return cache.tokens;
  }

  // A previous whole-document lex can't be extended as a fragment prefix, and
  // `text` may not be an append at all (e.g. retry); start over in either case.
  if (cache.final || !text.startsWith(cache.source)) {
    cache.source = "";
    cache.tokens = [];
    cache.final = false;
  }

  const boundary = findStableBoundary(text, cache.source.length);
  if (boundary > cache.source.length) {
    for (
      const token of lexer(text.slice(cache.source.length, boundary), {
        gfm: true,
      })
    ) {
      cache.tokens.push(token);
    }
    cache.source = text.slice(0, boundary);
  }

  const tail = text.slice(cache.source.length);
  if (tail.length === 0) return cache.tokens;
  return [...cache.tokens, ...lexer(tail, { gfm: true })];
}

interface Fence {
  char: string;
  length: number;
}

function fenceOpen(line: string): Fence | null {
  const match = /^ {0,3}(`{3,}|~{3,})/.exec(line);
  const run = match?.[1];
  if (!run) return null;
  return { char: run[0] ?? "`", length: run.length };
}

function fenceClose(line: string, fence: Fence): boolean {
  const trimmed = line.replace(/^ {0,3}/, "").trimEnd();
  return (
    trimmed.length >= fence.length &&
    [...trimmed].every((char) => char === fence.char)
  );
}

/** A line that could be re-joined to the previous block by later content. */
function isUnsafeLine(line: string): boolean {
  if (/^\s/.test(line)) return true; // indented continuation / code
  if (/^[-*+]\s/.test(line)) return true; // bullet
  if (/^\d+[.)]\s/.test(line)) return true; // ordered item
  if (line.startsWith(">")) return true; // blockquote
  return false;
}

/**
 * Index just past the last blank-line boundary that will not be changed by
 * following content. Returns `from` when there is no such boundary yet.
 */
export function findStableBoundary(text: string, from: number): number {
  let boundary = from;
  let pos = from;
  let fence: Fence | null = null;
  let hasContent = false;
  let safe = true;

  while (pos < text.length) {
    const newline = text.indexOf("\n", pos);
    const end = newline === -1 ? text.length : newline;
    const line = text.slice(pos, end);

    if (fence) {
      hasContent = true;
      safe = false;
      if (fenceClose(line, fence)) fence = null;
    } else if (line.trim() === "") {
      if (hasContent && safe && newline !== -1) boundary = end + 1;
      hasContent = false;
      safe = true;
    } else {
      hasContent = true;
      const opener = fenceOpen(line);
      if (opener) {
        fence = opener;
        safe = false;
      } else if (isUnsafeLine(line)) {
        safe = false;
      }
    }

    if (newline === -1) break;
    pos = end + 1;
  }

  return boundary;
}
